import React, { useEffect, useState } from 'react';
import CusButton from '../../../components/material-ui/CusButton';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import Grid from '@mui/material/Grid2';
import SelectGroup from '../../../components/casino/SelectGroup';
import CusAutocomplete from '../../../components/material-ui/CusAutocomplete';
import CusDateTimePicker from '../../../components/material-ui/CusDateTimePicker';
import { getOnlyData } from '../../../utils';
import dayjs from 'dayjs';
import { ENUM_DEPOSIT_STATUS, sourcePlatformOptions } from '../../../enums/enums';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../store';
import { useSessionStorage } from 'usehooks-ts';
import UserAutoComplete from '../../../components/material-ui/userAutoComplete';
import DatePreset from '../../../components/material-ui/DatePreset';
import useBootstrapSize from '../../../hooks/useBootstrapSize';
import CurrencySelect, { ENUM_CURRENCY } from '../../../components/shared/CurrencySelect';
import { Box, Stack } from '@mui/material';
import BoxGrayContainer from '../../../components/material-ui/BoxGrayContainer';
import CusInput from '../../../components/material-ui/CusInput';
import ShowContainer from '../../../components/material-ui/ShowContainer';

export default function DepositsFilter({
  onSaveClick,
  gates,
  statusHash,
  btnLoading,
  updatePlayers
}) {
  const { t } = useTranslation();
  const prevWeek = getOnlyData(dayjs(), '00:00:00');
  const today = getOnlyData(dayjs(), '23:59:59');
  const { isMobile } = useBootstrapSize();

  const getLabelStatus = (el) => ENUM_DEPOSIT_STATUS[el] || el;

  const players = useAppSelector((state) => state.finance.deposit.players);
  const mainGroup = useAppSelector((state) => state.user.group.main);
  const depositFilter = useAppSelector((state) => state.finance.deposit.filter);

  const mainGroupId = mainGroup.groupId;

  const [selectGroupId, setSelectGroupId] = useState(depositFilter.groupId ?? mainGroupId);
  const [selectPlayer, setSelectPlayer] = useState(depositFilter.userId ?? 0);
  const [typeGate, setTypeGate] = useState(depositFilter.gateway ?? 'any');
  const [status, setStatus] = useState(depositFilter.status ?? 2);
  const [source, setSource] = useState(depositFilter.source ?? 'any');
  const [currency, setCurrency] = useState(depositFilter.currency ?? ENUM_CURRENCY.all);
  const [startDate, setStartDate] = useState(depositFilter.dateFrom ?? prevWeek);
  const [endDate, setEndDate] = useState(depositFilter.dateTo ?? today);
  const [requestId, setRequestId] = useState(depositFilter.requestIdFind || '');

  const playersGroup = players[selectGroupId] || [];
  const playersOptions = playersGroup.map((el) => ({ label: el.login, value: el.id }));
  const gatesOptions = gates.map((el) => ({ label: el.label || 'Any', value: el.code }));
  const statusesOptions = Object.keys(statusHash).map?.((id) => ({
    label: getLabelStatus(statusHash[id]),
    value: +id
  }));
  playersOptions.unshift({ label: t('All'), value: 0 });

  const gridSize = { xs: 12, sm: 6 };
  const isUseFind = requestId.length >= 3;

  useEffect(() => {
    if (selectGroupId) return;
    setSelectGroupId(mainGroupId);
  }, [selectGroupId, mainGroupId]);

  useEffect(() => {
    if (!selectGroupId) return;
    updatePlayers(selectGroupId);
  }, [selectGroupId]);

  const FindButton = () => (
    <CusButton
      text="Show"
      variant="contained"
      fullWidth
      onClick={() =>
        onSaveClick({
          groupId: selectGroupId,
          userId: selectPlayer,
          dateFrom: startDate,
          dateTo: endDate,
          gateway: typeGate,
          status: status,
          source: source,
          currency: currency,
          requestIdFind: requestId
        })
      }
      disabled={!gates.length || !Object.keys(statusHash).length || btnLoading}
    />
  );

  return (
    <Stack gap={1}>
      <Grid container rowSpacing={2} columnSpacing={1}>
        <Grid size={12}>
          <SelectGroup
            title="Group"
            hideTooltip
            selectGroupId={selectGroupId}
            setSelectGroupId={(val) => {
              setSelectPlayer(0);
              setSelectGroupId(val);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <UserAutoComplete
            groupId={selectGroupId}
            options={playersOptions}
            userId={selectPlayer}
            setUserId={setSelectPlayer}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <CusLabelSelect
            label={'Site/ tg bot'}
            value={source}
            options={sourcePlatformOptions}
            onChange={setSource}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <CurrencySelect isChooseAll value={currency} setValue={setCurrency} />
        </Grid>
        <Grid size={gridSize}>
          <CusDateTimePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            formatChange="YYYY-MM-DD HH:mm:ss"
          />
        </Grid>
        <Grid size={gridSize}>
          <CusDateTimePicker
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            formatChange="YYYY-MM-DD HH:mm:ss"
            errorText={
              startDate && endDate && new Date(endDate).getTime() <= new Date(startDate).getTime()
                ? t('End date must be later than start date')
                : ''
            }
          />
        </Grid>
        <Grid size={12} mt={-0.5}>
          <DatePreset
            fontSize={isMobile ? 12 : 14}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
        </Grid>
        <Grid size={gridSize}>
          <CusLabelSelect
            label={'Invoice status'}
            value={status}
            options={statusesOptions}
            onChange={setStatus}
          />
        </Grid>
        <Grid size={gridSize}>
          <CusLabelSelect
            label={'Gateway name'}
            value={typeGate}
            options={gatesOptions}
            onChange={setTypeGate}
          />
        </Grid>
      </Grid>
      <Grid spacing={1} container alignItems={'end'}>
        <Grid size={'grow'}>
          <BoxGrayContainer p={1}>
            <Grid container spacing={1} alignItems={'end'}>
              <Grid size={'grow'}>
                <CusInput
                  isRow
                  width={'grow'}
                  label={'Request ID'}
                  value={requestId}
                  onChange={(e) => setRequestId(e.target.value)}
                  inputSx={{ flex: 1 }}
                  tooltipText={
                    'To search by request ID, enter a request ID of at least 3 characters'
                  }
                />
              </Grid>
              <ShowContainer condition={isUseFind}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FindButton />
                </Grid>
              </ShowContainer>
            </Grid>
          </BoxGrayContainer>
        </Grid>
        <ShowContainer condition={!isUseFind}>
          <Grid size={{ xs: 12, sm: 6 }}>
            {/* что бы не прыгала при смене isUseFind */}
            <Box p={1} pl={0}>
              <FindButton />
            </Box>
          </Grid>
        </ShowContainer>
      </Grid>
    </Stack>
  );
}
